import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Spinner from "../../components/Spinner";
import {
  confirmPublicInvitation,
  getPublicInvitation,
  reservePublicInvitationSlot,
  type ParentMeetingSlot,
  type PublicParentMeetingInvitation,
} from "../../services/parentMeetingService";

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat("nl-NL", {
    weekday: "long",
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

export default function PublicParentMeetingConfirmation() {
  const { token } = useParams<{ token: string }>();
  const [invitation, setInvitation] = useState<PublicParentMeetingInvitation | null>(null);
  const [selectedSlotId, setSelectedSlotId] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reserving, setReserving] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [reserved, setReserved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedSlot = useMemo<ParentMeetingSlot | undefined>(
    () => invitation?.availableSlots.find((slot) => slot.id === selectedSlotId),
    [invitation, selectedSlotId]
  );

  useEffect(() => {
    if (!token) {
      setError("Deze uitnodiging is ongeldig.");
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await getPublicInvitation(token);
        if (cancelled) return;
        setInvitation(data);
        setEmail(data.currentEmail || "");
        setSelectedSlotId(data.selectedSlot?.id || "");
        setReserved(Boolean(data.selectedSlot));
      } catch (err) {
        console.error(err);
        if (!cancelled) setError("De uitnodiging kon niet worden opgehaald of is verlopen.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleSlotChange(slotId: string) {
    setSelectedSlotId(slotId);
    setReserved(false);
    if (!token || !slotId) return;
    setReserving(true);
    setError(null);
    try {
      await reservePublicInvitationSlot(token, { slotId });
      setReserved(true);
    } catch (err) {
      console.error(err);
      setError("Dit tijdslot is helaas niet meer beschikbaar.");
      try {
        const data = await getPublicInvitation(token);
        setInvitation(data);
        setSelectedSlotId("");
        setReserved(false);
      } catch (reloadErr) {
        console.error(reloadErr);
      }
    } finally {
      setReserving(false);
    }
  }

  async function handleConfirm(event: React.FormEvent) {
    event.preventDefault();
    if (!token || !selectedSlotId) return;

    setSaving(true);
    setError(null);

    try {
      await confirmPublicInvitation(token, {
        slotId: selectedSlotId,
        email,
      });
      setConfirmed(true);
    } catch (err) {
      console.error(err);
      setError("De afspraak kon niet worden bevestigd. Mogelijk is dit tijdslot net gekozen door iemand anders.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <main className="min-h-screen bg-gray-50 px-4 py-8 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
      <div className="mx-auto max-w-2xl rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
        {loading && <Spinner />}

        {!loading && error && (
          <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-300">{error}</div>
        )}

        {!loading && confirmed && (
          <div className="rounded-lg bg-green-50 p-5 text-green-800 dark:bg-green-900/30 dark:text-green-300">
            <h1 className="text-xl font-semibold">Afspraak bevestigd</h1>
            <p className="mt-2 text-sm">U ontvangt een bevestiging per e-mail. De docent ontvangt deze bevestiging ook.</p>
          </div>
        )}

        {!loading && !confirmed && invitation && (
          <form onSubmit={handleConfirm} className="flex flex-col gap-5">
            <div>
              <h1 className="text-2xl font-semibold">{invitation.title}</h1>
              {invitation.description && <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{invitation.description}</p>}
              {(invitation.childName || invitation.parentName) && (
                <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                  {invitation.childName && <>Voor: <strong>{invitation.childName}</strong></>}
                  {invitation.childName && invitation.parentName && " · "}
                  {invitation.parentName && <>Ouder: <strong>{invitation.parentName}</strong></>}
                </p>
              )}
            </div>

            <label className="flex flex-col gap-2 text-sm">
              <span className="font-medium">Kies een tijdslot</span>
              <select
                value={selectedSlotId}
                onChange={(event) => handleSlotChange(event.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-950"
                required
              >
                <option value="">Selecteer een tijdslot</option>
                {invitation.availableSlots.map((slot) => (
                  <option key={slot.id} value={slot.id}>
                    {formatDateTime(slot.startTime)} - {formatDateTime(slot.endTime)}
                  </option>
                ))}
              </select>
            </label>

            {selectedSlot && (
              <div className="rounded-lg bg-blue-50 p-4 text-sm text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                {reserved ? "Dit tijdslot is tijdelijk voor u gereserveerd." : "Tijdslot reserveren..."}{" "}
                <strong>{formatDateTime(selectedSlot.startTime)}</strong>
              </div>
            )}

            <label className="flex flex-col gap-2 text-sm">
              <span className="font-medium">E-mailadres voor bevestiging</span>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-950"
                required
              />
              <span className="text-xs text-gray-500 dark:text-gray-400">
                U mag dit e-mailadres wijzigen. De bevestiging wordt naar dit adres gestuurd.
              </span>
            </label>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving || reserving || !reserved || invitation.availableSlots.length === 0}
                className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {reserving ? "Tijdslot reserveren..." : "Afspraak bevestigen"}
              </button>
            </div>
          </form>
        )}
      </div>
    </main>
  );
}
